/**
 * Raport zarządu — teksty sekcji report (`mbr.report.*`). WŁAŚCICIEL: agent sekcji report (docs/RAPORT-ZARZADU.md).
 * Podpięte już w i18n/mbr.ts — dopisuj klucze tutaj, nie w extra.ts. `en` typowane względem `pl`.
 * Własne kody uzgodnień sekcji: `checks: { KOD: '…' }` (panel szuka `mbr.checks.KOD`, potem `mbr.report.checks.KOD`).
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const mbrReportPl = {
  title: 'Pulpit miesiąca',
  description: 'Najważniejsze wskaźniki wszystkich sekcji, wnioski i komentarz zarządczy.',
  tilesTitle: 'Najważniejsze wskaźniki',
  tilesEmpty: 'Sekcje nie zwróciły jeszcze wskaźników z danymi dla tego okresu.',
  frozen: 'Dane zamrożone przy zamknięciu miesiąca',
  banner: {
    NONE: 'Miesiąc nie został jeszcze otwarty w zamknięciu',
    DRAFT: 'Zamknięcie miesiąca w przygotowaniu',
    IN_REVIEW: 'Zamknięcie miesiąca w weryfikacji',
    CLOSED: 'Miesiąc zamknięty',
    forms: 'Formularze działów: {done} z {total}',
    missing: 'brakuje: {list}',
    overdue: 'po terminie: {count}',
    checks: 'Uzgodnienia: {fail} rozbieżności, {warn} do sprawdzenia',
    checksOk: 'Uzgodnienia bez rozbieżności',
    sent: 'Wysłany do zarządu {date} ({count} odbiorców)',
    notSent: 'Jeszcze nie wysłany do zarządu',
    goClose: 'Przejdź do zamknięcia',
    unavailable: 'Nie udało się policzyć sekcji: {list}',
  },
  pulse: {
    tilesSubtitle: 'Kolor paska = ocena zmiany dla firmy, nie kierunek liczby',
    closeTitle: 'Zamknięcie miesiąca',
    closeForms: 'Formularze: {done} z {total}',
    closeMissing: 'Brakuje: {list}',
    closeOverdue: '{count} po terminie',
    closeChecks: 'Uzgodnienia',
    closeSent: 'Wysłano do zarządu {date}',
    closeNotSent: 'Jeszcze nie wysłano do zarządu',
    closeOpen: 'Otwórz zamknięcie',
    closeUnavailable: 'Nie policzono sekcji: {list}',
    previewTitle: 'Sekcje raportu',
    previewSubtitle: 'Kluczowa liczba każdej zakładki i jej uzgodnienia',
    previewOpen: 'Otwórz zakładkę {tab}',
    previewNoData: 'brak danych',
    closeKpi: 'formularze',
  },
  findingsTitle: 'Wnioski',
  findingsEmpty: 'Brak istotnych zmian ani rozbieżności.',
  findingTone: { GOOD: 'Dobrze', BAD: 'Uwaga', WARN: 'Do sprawdzenia', INFO: 'Informacja' },
  findings: {
    kpiWorseTitle: '{label}: zmiana na niekorzyść',
    kpiBetterTitle: '{label}: zmiana na korzyść',
    kpiChangePct: '{direction} o {change}% względem okresu bazowego.',
    kpiChangePp: '{direction} o {change} pp względem okresu bazowego.',
    kpiChangeAbs: '{direction} o {amount} względem okresu bazowego.',
    up: 'Wzrost',
    down: 'Spadek',
    checkFailTitle: 'Rozbieżność w danych',
    checkFailBody: '{message}',
    closeOpenTitle: 'Miesiąc nie jest zamknięty',
    closeOpenBody:
      'Brakuje {missing} z {total} formularzy działów (po terminie: {overdue}). Raport do zarządu wyjdzie po zamknięciu.',
    unavailableTitle: 'Sekcja niedostępna',
    unavailableBody: 'Sekcji „{section}” nie udało się policzyć — pozostałe wskaźniki są aktualne.',
    missingTitle: 'Brak danych w sekcji',
    missingBody: 'Sekcja „{section}” nie ma danych za ten okres. Brak danych to nie zero.',
    open: 'Pokaż',
  },
  ai: {
    title: 'Komentarz zarządczy',
    subtitle: '5 zdań „co się zmieniło i dlaczego” + 3 rekomendacje. Każda liczba pochodzi z danych raportu.',
    changes: 'Co się zmieniło i dlaczego',
    recommendations: 'Rekomendacje',
    empty: 'Komentarz powstanie automatycznie przy zamknięciu miesiąca. Możesz go też wygenerować teraz.',
    generate: 'Wygeneruj komentarz',
    regenerate: 'Odśwież komentarz',
    generating: 'Generowanie komentarza…',
    generated: 'Komentarz zapisany',
    sourceModel: 'Model AI · liczby sprawdzone z danymi',
    sourceTemplate: 'Złożony z danych raportu (szablon)',
    mock: 'Tryb demonstracyjny — bez modelu językowego',
    frozen: 'z zamrożonych danych miesiąca',
    generatedAt: 'Wygenerowano {date}',
    restricted: 'Komentarz zarządczy widzą osoby z dostępem do kosztów i wyniku.',
    error: 'Nie udało się wygenerować komentarza.',
  },
  actions: {
    exportPdf: 'PDF',
    exportXlsx: 'Excel',
    exportHint: 'Pobierz raport w Twoim zakresie danych',
    preparing: 'Przygotowuję plik…',
    downloaded: 'Plik pobrany',
    exportError: 'Nie udało się przygotować pliku.',
    send: 'Wyślij do zarządu',
    sendTest: 'Wyślij próbnie do mnie',
    sendConfirm: 'Wysłać raport za {period} do {count} odbiorców z ustawień?',
    sent: 'Wysłano do {delivered} z {recipients} odbiorców',
    sentTest: 'Wysłano próbnie na Twój adres',
    sendError: 'Nie udało się wysłać raportu.',
    sendNeedsClose: 'Wysyłka do zarządu po zamknięciu miesiąca',
  },
  schedule: {
    title: 'Wysyłka do zarządu',
    state: {
      SENT: 'Wysłany',
      SCHEDULED: 'Zaplanowany',
      WAITING_FOR_CLOSE: 'Czeka na zamknięcie miesiąca',
      OVERDUE: 'Po terminie — miesiąc niezamknięty',
      DISABLED: 'Wysyłka automatyczna wyłączona',
      NO_RECIPIENTS: 'Brak odbiorców',
    },
    when: '{day}. dzień roboczy, godz. {hour}:00 — {date}',
  },
  settingsTitle: 'Ustawienia raportu',
  settingsStub:
    'Ustawienia (próg utraty klienta, tolerancje, klucz podziału, odbiorcy, harmonogram) — w budowie.',
  settings: {
    description:
      'Progi i reguły liczenia raportu oraz wysyłka do zarządu. Zmiana działa od razu na wszystkie niezamknięte miesiące.',
    readOnly: 'Ustawienia zmienia osoba z uprawnieniem „Raport zarządu — ustawienia”.',
    save: 'Zapisz ustawienia',
    saved: 'Ustawienia zapisane',
    saveError: 'Nie udało się zapisać ustawień.',
    reset: 'Cofnij zmiany',
    rules: 'Reguły liczenia',
    lostAfterMonths: 'Klient utracony po (pełnych miesiącach bez zamówienia)',
    alternatingMinSwitches: 'Klient „wahadłowy” od (przełączeń w 6 mies.)',
    flatDeltaPct: 'Zmiana „bez istotnej zmiany” poniżej (% / pp)',
    driverHoursAlert: 'Alert godzin kierowcy powyżej (h / mies.)',
    logisticsScope: 'Pojazdy logistyki',
    logisticsScopeHint:
      'Typy pojazdów liczone do km, paliwa, godzin kierowców, km / zam., h / zam. i alertu godzin. Pozostałe (domyślnie osobowe handlowców) raport pokazuje osobno, poza sumami. Pojazd przypisany do aktywnej trasy liczy się zawsze.',
    tolerance: 'Tolerancja uzgodnień',
    toleranceHint: 'Rozbieżność dopiero po przekroczeniu obu progów; w progach — „do sprawdzenia”.',
    toleranceAbs: 'Kwotowa (zł)',
    tolerancePct: 'Procentowa (%)',
    allocation: 'Klucz podziału kosztów centrali na oddziały',
    allocationKey: {
      SALES: 'Udział w sprzedaży',
      ORDERS: 'Udział w zamówieniach',
      KM: 'Udział w kilometrach',
    },
    routes: 'Trasy → oddział',
    routesHint:
      'Grupa tras z dokumentów sprzedaży i oddział, do którego się liczy (udział oddziału do całości firmy).',
    routeGroup: 'Grupa tras',
    branch: 'Oddział',
    addRoute: 'Dodaj grupę',
    removeRoute: 'Usuń',
    mail: 'Wysyłka do zarządu',
    sendEnabled: 'Wysyłaj automatycznie po zamknięciu miesiąca',
    sendBusinessDay: 'Dzień roboczy miesiąca',
    sendHour: 'Godzina (czas warszawski)',
    recipients: 'Odbiorcy (adresy e-mail, każdy w nowej linii)',
    recipientsInvalid: 'Niepoprawne adresy: {list}',
    mailHint:
      'Raport (PDF) wychodzi tylko dla zamkniętego miesiąca. Do tego czasu osoby zamykające miesiąc dostają przypomnienie.',
  },
};

export const mbrReportEn: Shape<typeof mbrReportPl> = {
  title: 'Month dashboard',
  description: 'Key indicators of every section, findings and the management commentary.',
  tilesTitle: 'Key indicators',
  tilesEmpty: 'The sections have not returned indicators with data for this period yet.',
  frozen: 'Data frozen at the month close',
  banner: {
    NONE: 'The month has not been opened in the close yet',
    DRAFT: 'Month close in preparation',
    IN_REVIEW: 'Month close under review',
    CLOSED: 'Month closed',
    forms: 'Department forms: {done} of {total}',
    missing: 'missing: {list}',
    overdue: 'overdue: {count}',
    checks: 'Reconciliation: {fail} discrepancies, {warn} to check',
    checksOk: 'Reconciliation without discrepancies',
    sent: 'Sent to the board {date} ({count} recipients)',
    notSent: 'Not sent to the board yet',
    goClose: 'Go to the close',
    unavailable: 'Could not compute sections: {list}',
  },
  pulse: {
    tilesSubtitle: 'Bar colour = how the change is for the company, not the number’s direction',
    closeTitle: 'Month close',
    closeForms: 'Forms: {done} of {total}',
    closeMissing: 'Missing: {list}',
    closeOverdue: '{count} overdue',
    closeChecks: 'Checks',
    closeSent: 'Sent to the board {date}',
    closeNotSent: 'Not sent to the board yet',
    closeOpen: 'Open month close',
    closeUnavailable: 'Sections not computed: {list}',
    previewTitle: 'Report sections',
    previewSubtitle: 'The key number of each tab and its checks',
    previewOpen: 'Open tab {tab}',
    previewNoData: 'no data',
    closeKpi: 'forms',
  },
  findingsTitle: 'Findings',
  findingsEmpty: 'No material changes or discrepancies.',
  findingTone: { GOOD: 'Good', BAD: 'Attention', WARN: 'To check', INFO: 'Info' },
  findings: {
    kpiWorseTitle: '{label}: change for the worse',
    kpiBetterTitle: '{label}: change for the better',
    kpiChangePct: '{direction} by {change}% vs the base period.',
    kpiChangePp: '{direction} by {change} pp vs the base period.',
    kpiChangeAbs: '{direction} by {amount} vs the base period.',
    up: 'Up',
    down: 'Down',
    checkFailTitle: 'Data discrepancy',
    checkFailBody: '{message}',
    closeOpenTitle: 'The month is not closed',
    closeOpenBody:
      '{missing} of {total} department forms are missing (overdue: {overdue}). The board report goes out after the close.',
    unavailableTitle: 'Section unavailable',
    unavailableBody: 'Section “{section}” could not be computed — the other indicators are up to date.',
    missingTitle: 'No data in the section',
    missingBody: 'Section “{section}” has no data for this period. Missing data is not zero.',
    open: 'Show',
  },
  ai: {
    title: 'Management commentary',
    subtitle:
      '5 sentences “what changed and why” + 3 recommendations. Every number comes from the report data.',
    changes: 'What changed and why',
    recommendations: 'Recommendations',
    empty: 'The commentary is created automatically at the month close. You can also generate it now.',
    generate: 'Generate commentary',
    regenerate: 'Refresh commentary',
    generating: 'Generating the commentary…',
    generated: 'Commentary saved',
    sourceModel: 'AI model · numbers checked against the data',
    sourceTemplate: 'Composed from the report data (template)',
    mock: 'Demo mode — no language model',
    frozen: 'from the frozen month data',
    generatedAt: 'Generated {date}',
    restricted: 'The management commentary is visible to people with access to costs and results.',
    error: 'Could not generate the commentary.',
  },
  actions: {
    exportPdf: 'PDF',
    exportXlsx: 'Excel',
    exportHint: 'Download the report within your data scope',
    preparing: 'Preparing the file…',
    downloaded: 'File downloaded',
    exportError: 'Could not prepare the file.',
    send: 'Send to the board',
    sendTest: 'Send a test to me',
    sendConfirm: 'Send the report for {period} to {count} recipients from the settings?',
    sent: 'Sent to {delivered} of {recipients} recipients',
    sentTest: 'Test sent to your address',
    sendError: 'Could not send the report.',
    sendNeedsClose: 'Sending to the board after the month close',
  },
  schedule: {
    title: 'Board e-mail',
    state: {
      SENT: 'Sent',
      SCHEDULED: 'Scheduled',
      WAITING_FOR_CLOSE: 'Waiting for the month close',
      OVERDUE: 'Overdue — month not closed',
      DISABLED: 'Automatic sending off',
      NO_RECIPIENTS: 'No recipients',
    },
    when: 'business day {day}, {hour}:00 — {date}',
  },
  settingsTitle: 'Report settings',
  settingsStub:
    'Settings (lost-customer threshold, tolerances, allocation key, recipients, schedule) — under construction.',
  settings: {
    description:
      'Thresholds and rules of the report and the board e-mail. A change applies at once to every month not closed.',
    readOnly: 'Settings are changed by someone with the “Management report — settings” permission.',
    save: 'Save settings',
    saved: 'Settings saved',
    saveError: 'Could not save the settings.',
    reset: 'Undo changes',
    rules: 'Calculation rules',
    lostAfterMonths: 'Customer lost after (full months without an order)',
    alternatingMinSwitches: '“Alternating” customer from (switches in 6 months)',
    flatDeltaPct: '“No material change” below (% / pp)',
    driverHoursAlert: 'Driver hours alert above (h / month)',
    logisticsScope: 'Logistics vehicles',
    logisticsScopeHint:
      'Vehicle types counted in km, fuel, driver hours, km / order, h / order and the hours alert. Others (by default the sales reps’ passenger cars) are shown apart, outside the totals. A vehicle assigned to an active route always counts.',
    tolerance: 'Reconciliation tolerance',
    toleranceHint: 'A discrepancy only above both thresholds; within them — “to check”.',
    toleranceAbs: 'Amount (PLN)',
    tolerancePct: 'Percent (%)',
    allocation: 'HQ cost allocation key to branches',
    allocationKey: { SALES: 'Share of sales', ORDERS: 'Share of orders', KM: 'Share of kilometres' },
    routes: 'Routes → branch',
    routesHint:
      'Route group from sales documents and the branch it counts to (branch share of the whole company).',
    routeGroup: 'Route group',
    branch: 'Branch',
    addRoute: 'Add group',
    removeRoute: 'Remove',
    mail: 'Board e-mail',
    sendEnabled: 'Send automatically after the month close',
    sendBusinessDay: 'Business day of the month',
    sendHour: 'Hour (Warsaw time)',
    recipients: 'Recipients (e-mail addresses, one per line)',
    recipientsInvalid: 'Invalid addresses: {list}',
    mailHint:
      'The report (PDF) goes out only for a closed month. Until then the people closing the month get a reminder.',
  },
};
